import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

// Four quadrants, one per element. `room` is the installed photograph shown in
// the enlarged tile; `swatch` is the material texture shown while it is small.
const TILES = [
  {
    key: "Timber",
    slug: "timber",
    tag: "Engineered oak · wide-plank · parquet",
    line: "Warmth underfoot, and the one element that ties an open plan together.",
    room: "/journal/timber-guide.jpg",
    swatch: "/timber_boston_oak.jpg",
  },
  {
    key: "Carpet",
    slug: "carpet",
    tag: "Wool · wool-rich · hybrid",
    line: "The quietest decision in a house, and the one you feel every morning.",
    room: "/journal/carpet-guide.jpg",
    swatch: "/carpet.jpg",
  },
  {
    key: "Tile",
    slug: "tile",
    tag: "Porcelain · terrazzo · handmade",
    line: "Fewer joints, calmer rooms — specified with the slip rating it needs.",
    room: "/journal/tile-guide.jpg",
    swatch: "/tile_botticino.jpg",
  },
  {
    key: "Stone",
    slug: "stone",
    tag: "Limestone · marble · bluestone",
    line: "The only material in the house that is genuinely old.",
    room: "/journal/stone-guide.jpg",
    swatch: "/stone_taj_mahal.jpg",
  },
];

const DWELL = 5200;

// Grid placement: the active tile fills the left two columns, the other three
// stack down the third column in their original order. Swapping which tile is
// active moves every tile — framer-motion's `layout` does the morph.
const areaFor = (index, activeIndex) => {
  if (index === activeIndex) return { gridColumn: "1 / 3", gridRow: "1 / 4" };
  const others = TILES.map((_, i) => i).filter((i) => i !== activeIndex);
  const row = others.indexOf(index) + 1;
  return { gridColumn: "3 / 4", gridRow: `${row} / ${row + 1}` };
};

export default function ElementQuadrants() {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduce, setReduce] = useState(false);
  const resume = useRef(null);
  // Clicking holds the chosen element for a while, then the loop resumes.
  const hold = (i) => {
    setActive(i);
    setPaused(true);
    clearTimeout(resume.current);
    resume.current = setTimeout(() => setPaused(false), 15000);
  };
  useEffect(() => () => clearTimeout(resume.current), []);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "start center"] });
  const bg = useTransform(scrollYProgress, [0, 1], ["#121315", "#F5F4F0"]);
  const headColor = useTransform(scrollYProgress, [0, 0.7], ["#F5F4F0", "#121315"]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const onMq = (e) => setReduce(e.matches);
    mq.addEventListener("change", onMq);
    return () => mq.removeEventListener("change", onMq);
  }, []);

  useEffect(() => {
    if (paused || reduce) return;
    const t = setTimeout(() => setActive((a) => (a + 1) % TILES.length), DWELL);
    return () => clearTimeout(t);
  }, [active, paused, reduce]);

  const item = TILES[active];

  return (
    <motion.section
      id="disciplines"
      ref={ref}
      style={{ backgroundColor: bg }}
      className="relative py-28 md:py-40 cv-auto"
      data-testid="element-quadrants"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <p className="text-[#6E5A40] text-[11px] tracking-[0.4em] uppercase mb-6">The Gallery</p>
            <motion.h2
              style={{ color: headColor }}
              className="font-serif-display font-light tracking-tight text-4xl sm:text-5xl md:text-6xl leading-[1.02] max-w-2xl"
            >
              Four elements,<br />one moving picture
            </motion.h2>
          </div>
          <motion.p style={{ color: headColor }} className="text-sm max-w-xs font-body opacity-80">
            The quadrants turn on their own. Tap one to hold it, or open the element for the full
            range.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Element index */}
          <div className="md:col-span-4 order-2 md:order-1">
            <div className="divide-y divide-[#3B3E42]/15 border-t border-b border-[#3B3E42]/15">
              {TILES.map((t, i) => {
                const on = i === active;
                return (
                  <button
                    key={t.key}
                    data-testid={`quadrant-select-${t.slug}`}
                    onClick={() => hold(i)}
                    className="w-full text-left py-5 md:py-6 group"
                    aria-current={on}
                  >
                    <span className="flex items-baseline justify-between gap-4">
                      <span
                        className="font-serif-display text-3xl md:text-4xl font-light tracking-tight"
                        style={{ color: on ? "#6E5A40" : "#121315", transition: "color 0.5s ease" }}
                      >
                        {t.key}
                      </span>
                      <ArrowUpRight
                        size={20}
                        className="text-[#6E5A40]"
                        style={{ opacity: on ? 1 : 0.25, transition: "opacity 0.5s ease" }}
                      />
                    </span>
                    {/* progress hairline for the active row */}
                    <span className="mt-3 block h-[2px] w-full bg-[#3B3E42]/12 overflow-hidden">
                      <motion.span
                        key={`${t.key}-${active}-${paused}`}
                        className="block h-full bg-[#6E5A40]"
                        initial={{ width: on ? "0%" : "0%" }}
                        animate={{ width: on ? "100%" : "0%" }}
                        transition={{
                          duration: on && !paused && !reduce ? DWELL / 1000 : 0.5,
                          ease: "linear",
                        }}
                      />
                    </span>
                  </button>
                );
              })}
            </div>

            <Link
              to={`/${item.slug}`}
              data-testid={`quadrant-explore-${item.slug}`}
              className="inline-block mt-8 text-[#6E5A40] text-[10px] tracking-[0.3em] uppercase link-underline"
              style={{ transition: "color 0.4s ease" }}
            >
              Explore {item.key} →
            </Link>
          </div>

          {/* The morphing quadrant block */}
          <div className="md:col-span-8 order-1 md:order-2">
            <div
              className="relative grid gap-2 md:gap-3 aspect-[4/3] w-full"
              style={{ gridTemplateColumns: "1fr 1fr 0.62fr", gridTemplateRows: "1fr 1fr 1fr" }}
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              {TILES.map((t, i) => {
                const on = i === active;
                return (
                  <motion.button
                    key={t.key}
                    layout
                    transition={{ duration: reduce ? 0 : 0.9, ease: [0.22, 1, 0.36, 1] }}
                    style={areaFor(i, active)}
                    onClick={() => hold(i)}
                    data-testid={`quadrant-tile-${t.slug}`}
                    aria-label={`Show ${t.key}`}
                    className="relative overflow-hidden bg-[#121315] group"
                  >
                    <img
                      src={t.swatch}
                      alt=""
                      aria-hidden
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <motion.img
                      src={t.room}
                      alt={`${t.key} installed in a luxury Sydney interior`}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover"
                      animate={{ opacity: on ? 1 : 0 }}
                      transition={{ duration: reduce ? 0 : 0.9, ease: [0.22, 1, 0.36, 1] }}
                    />
                    <span
                      className="absolute inset-0"
                      style={{
                        background: on
                          ? "linear-gradient(to top, rgba(18,19,21,0.72) 0%, rgba(18,19,21,0.05) 55%)"
                          : "rgba(18,19,21,0.42)",
                        transition: "background 0.7s ease",
                      }}
                    />
                    <span className="absolute inset-2 border border-[#F5F4F0]/12 pointer-events-none" />
                    {on ? (
                      <motion.span
                        key={t.key}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute left-5 md:left-7 bottom-5 md:bottom-7 text-left"
                      >
                        <span className="block font-serif-display text-[#F5F4F0] text-3xl md:text-5xl font-light leading-none">
                          {t.key}
                        </span>
                        <span className="block text-[#C9A876] text-[9px] md:text-[10px] tracking-[0.3em] uppercase mt-2">
                          {t.tag}
                        </span>
                        <span className="hidden sm:block text-[#EDE9E2]/80 text-xs md:text-sm font-body mt-3 max-w-sm">
                          {t.line}
                        </span>
                      </motion.span>
                    ) : (
                      <span className="absolute left-3 bottom-3 text-[#F5F4F0]/85 text-[8px] md:text-[9px] tracking-[0.3em] uppercase">
                        {t.key}
                      </span>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
